/**
 * ShippingEstimator Component (Final V4 - Split Shipment Support)
 * Version: 4.2.0 - 2026-07-27 (UTC+8) - locale-aware display_<locale> labels
 */
if (!customElements.get('shipping-estimator')) {
  class ShippingEstimator extends PopupBase {
    // Static properties
    static fetchPromise = null; 
    static parsedData = null;

    constructor() {
      super();
      
      // DOM Elements
      this.triggerBtn = this.querySelector('.shipping-location-trigger');
      this.locationText = this.querySelector('.location-name');
      this.timeText = this.querySelector('.time-text');
      this.template = this.querySelector('#shipping-estimator-popup-template');
      
      // Configuration
      this.storageKey = 'shipping_estimator_user_preference';
      this.defaultTextPattern = this.dataset.textPattern;
      this.fallbackText = this.dataset.textFallback;
      this.popupTitleText = this.dataset.popupTitle || 'Select your location';
      this.textDetecting = this.dataset.textDetecting || 'Detecting';
      this.textSelectLocation = this.dataset.textSelectLocation || 'Select Location';
      this.deliveryDatePattern = this.dataset.deliveryDatePattern; 
      
      // [新增] 拆单文案
      this.splitDatePattern = this.dataset.splitDatePattern; 
      
      this.locale = this.dataset.locale || 'en-US'; 
      this.currentState = null;

      this.init();
    }

    init() {
      // 1. 监听 Input 变化 (PDP变体切换)
      document.addEventListener('change', (e) => {
        if (e.target && e.target.name === 'id') {
          setTimeout(() => {
            const newVariantId = e.target.value;
            if (this.currentState) {
              this.updateEstimation(this.currentState, newVariantId);
            }
          }, 50);
        }
      });

      // 2. 监听 URL 变化 (PDP变体切换)
      const originalReplaceState = history.replaceState;
      history.replaceState = function() {
          originalReplaceState.apply(this, arguments);
          setTimeout(() => { document.dispatchEvent(new Event('variant:changed:url')); }, 50);
      };
      window.addEventListener('popstate', () => this.handleUrlChange());
      document.addEventListener('variant:changed:url', () => this.handleUrlChange());

      // 3. 监听组件间同步
      document.addEventListener('shipping:location-updated', (e) => {
        if (e.detail && e.detail.state !== this.currentState) {
          this.updateEstimation(e.detail.state);
        }
      });

      // 4. 初始化读取缓存
      const cachedState = localStorage.getItem(this.storageKey);
      const data = this.getShippingData();

      if (cachedState && data.regions && data.regions[cachedState]) {
        this.updateEstimation(cachedState);
      } else {
        this.detectLocation();
      }

      // 5. 绑定弹窗触发
      if (this.triggerBtn) {
        this.triggerBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.openLocationPopup();
        });
      }
    }

    handleUrlChange() {
      if (this.currentState) this.updateEstimation(this.currentState);
    }

    getShippingData() {
      if (ShippingEstimator.parsedData) return ShippingEstimator.parsedData;
      try {
        const rawData = this.dataset.shippingData;
        if (!rawData) return {};
        ShippingEstimator.parsedData = JSON.parse(rawData);
        return ShippingEstimator.parsedData;
      } catch (e) {
        console.error('Shipping Estimator: Invalid JSON.', e);
        return {};
      }
    }

    getRegionDisplayName(stateName) {
      const regions = this.getShippingData().regions || {};
      const regionConfig = regions[stateName];
      if (!regionConfig || typeof regionConfig !== 'object') return stateName;

      const localeCode = (this.locale || '').split(/[-_]/)[0].toLowerCase();
      const configuredName = regionConfig['display_' + localeCode] || regionConfig.display;
      return typeof configuredName === 'string' && configuredName.trim()
        ? configuredName.trim()
        : stateName;
    }

    escapeHtml(value) {
      const characters = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      };
      return String(value).replace(/[&<>"']/g, character => characters[character]);
    }

    detectLocation() {
      if (this.locationText) this.locationText.innerText = this.textDetecting;

      if (ShippingEstimator.fetchPromise) {
        ShippingEstimator.fetchPromise.then(region => {
          if (region) this.handleDetectionSuccess(region);
          else this.handleUnknownLocation();
        });
        return;
      }

      ShippingEstimator.fetchPromise = fetch('https://ipwho.is/')
        .then(response => response.json())
        .then(data => {
          if (data.success && data.region) return data.region;
          return null;
        })
        .catch(err => {
          console.warn('Location detection failed.', err);
          return null;
        });

      ShippingEstimator.fetchPromise.then(region => {
        if (region) this.handleDetectionSuccess(region);
        else this.handleUnknownLocation();
      });
    }

    handleDetectionSuccess(region) {
      const data = this.getShippingData();
      if (data && data.regions && data.regions[region]) {
        localStorage.setItem(this.storageKey, region);
        this.updateEstimation(region);
        this.broadcastUpdate(region);
      } else {
        this.handleUnknownLocation(region);
      }
    }

    broadcastUpdate(stateName) {
      const event = new CustomEvent('shipping:location-updated', {
        detail: { state: stateName }
      });
      document.dispatchEvent(event);
    }

    // 获取当前模式下的 Variant IDs (可能是单个，也可能是数组)
    getRelevantVariantIds(specificVariantId = null) {
      // 1. 如果显式传入了 ID (PDP Input Change)，优先级最高
      if (specificVariantId) return [specificVariantId];

      // 2. 检查是否在购物车模式
      if (this.dataset.mode === 'cart' && this.dataset.cartVariantIds) {
        try {
          return JSON.parse(this.dataset.cartVariantIds);
        } catch (e) {
          return [];
        }
      }

      // 3. PDP 模式：从 URL 获取
      const urlParams = new URLSearchParams(window.location.search);
      const variantFromUrl = urlParams.get('variant');
      if (variantFromUrl) return [variantFromUrl];

      // 4. PDP 模式：默认 ID
      return [this.dataset.variantId];
    }

    /**
     * [重构] 核心计算逻辑：支持 PDP 单品和 Cart 多品聚合
     */
    updateEstimation(stateName, specificVariantId = null) {
      this.currentState = stateName;
      
      if (this.locationText) {
        this.locationText.classList.remove('skeleton-loading');
        this.locationText.innerText = this.getRegionDisplayName(stateName);
      }

      if (this.timeText) {
        this.timeText.classList.remove('skeleton-loading');
        
        const allData = this.getShippingData(); 
        const variantIds = this.getRelevantVariantIds(specificVariantId);
        
        // 聚合器：记录所有商品的最小天数和最大天数
        let globalMinDays = Infinity;
        let globalMaxDays = -Infinity;
        let validItemCount = 0;

        // 如果是单品模式，顺便记录一下是否有自定义文案（用于优先级判断）
        let singleItemCustomText = null;

        // 用于判断购物车内不同商品的时效是否完全一致
        let isSplitShipment = false;
        let firstItemRawDays = null; 

        // --- 遍历所有商品进行计算 ---
        variantIds.forEach(vid => {
          let targetConfig = null;
          
          // A. Variant 级配置
          if (allData.variants && allData.variants[vid]) {
            targetConfig = allData.variants[vid];
          } 
          // B. Region 级配置
          else if (allData.regions && allData.regions[stateName]) {
            targetConfig = allData.regions[stateName];
          }

          if (targetConfig) {
            let rawDays = '';
            if (typeof targetConfig === 'object') {
              rawDays = String(targetConfig.days || '').trim();
              // locale-aware text lookup - 多语言文案优先级：text_<locale> → text - Plaud Tayler - 2026-07-20 14:30
              if (variantIds.length === 1) {
                const lcCode = (this.locale || '').split('-')[0].toLowerCase();
                singleItemCustomText = targetConfig['text_' + lcCode] || targetConfig.text;
              }
            } else {
              rawDays = String(targetConfig).trim();
            }

            // 解析天数 (e.g. "2-4" -> min:2, max:4)
            let min = 0, max = 0;
            if (rawDays.includes('-')) {
              const parts = rawDays.split('-');
              min = parseInt(parts[0]);
              max = parseInt(parts[1]);
            } else {
              min = parseInt(rawDays);
              max = min;
            }

            if (!isNaN(min) && !isNaN(max)) {
              // 更新全局极值计算
              if (min < globalMinDays) globalMinDays = min;
              if (max > globalMaxDays) globalMaxDays = max;
              validItemCount++;

              // 判断是否真的存在不同时效的商品 (引发拆单)
              if (firstItemRawDays === null) {
                firstItemRawDays = rawDays; // 记录第一个商品的原始时效配置 (如 "2" 或 "2-4")
              } else if (firstItemRawDays !== rawDays) {
                isSplitShipment = true; // 只要有任何一个商品的时效和第一个不同，就是拆单
              }
            }
          }
        });

        // --- 最终渲染逻辑 ---
        if (validItemCount > 0) {
          // 计算日期对象
          const dateMin = new Date(); dateMin.setDate(dateMin.getDate() + globalMinDays);
          const dateMax = new Date(); dateMax.setDate(dateMax.getDate() + globalMaxDays);
          
          const dateMinStr = this.formatDate(dateMin);
          const dateMaxStr = this.formatDate(dateMax);

          let message = '';

          // 场景 1: 单品模式 (且有自定义文案)
          if (variantIds.length === 1 && singleItemCustomText) {
             const rawDaysStr = globalMinDays === globalMaxDays ? `${globalMinDays}` : `${globalMinDays}-${globalMaxDays}`;
             const finalDateStr = globalMinDays === globalMaxDays ? dateMinStr : `${dateMinStr} - ${dateMaxStr}`;
             message = singleItemCustomText.replace('[date]', finalDateStr).replace('[days]', rawDaysStr);
          }
          // 场景 2: 拆单模式
          else if (variantIds.length > 1 && isSplitShipment) {
             if (this.splitDatePattern) {
               message = this.splitDatePattern
                 .replace('[date_min]', dateMinStr)
                 .replace('[date_max]', dateMaxStr);
             } else {
               // 回退显示
               message = `${dateMinStr} - ${dateMaxStr}`;
             }
          }
          // 场景 3: 标准模式 (单品无特殊文案，或多品但时间一致)
          else {
             const finalDateStr = globalMinDays === globalMaxDays ? dateMinStr : `${dateMinStr} - ${dateMaxStr}`;
             const rawDaysStr = globalMinDays === globalMaxDays ? `${globalMinDays}` : `${globalMinDays}-${globalMaxDays}`;
             
             if (this.deliveryDatePattern && this.deliveryDatePattern.includes('[date]')) {
                message = this.deliveryDatePattern.replace('[date]', finalDateStr);
             } else {
                message = this.defaultTextPattern.replace('[days]', rawDaysStr);
             }
          }

          this.timeText.innerText = message;
        } else {
          this.timeText.innerText = this.fallbackText;
        }
      }
    }

    formatDate(date) {
      if (this.locale.startsWith('en')) {
        const weekday = date.toLocaleString(this.locale, { weekday: 'long' });
        const month = date.toLocaleString(this.locale, { month: 'short' });
        const day = date.getDate();
        const suffix = this.getOrdinalSuffix(day);
        return `${weekday}, ${month} ${day}${suffix}`;
      } 
      return date.toLocaleString(this.locale, { month: 'short', day: 'numeric', weekday: 'long' });
    }

    getOrdinalSuffix(day) {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1:  return "st";
        case 2:  return "nd";
        case 3:  return "rd";
        default: return "th";
      }
    }

    handleUnknownLocation(regionName) {
      if (this.locationText) {
        this.locationText.classList.remove('skeleton-loading');
        this.locationText.innerText = regionName || this.textSelectLocation;
      }
      if (this.timeText) {
        this.timeText.classList.remove('skeleton-loading');
        this.timeText.innerText = this.fallbackText;
      }
    }

    openLocationPopup() {
      if (!this.template) return;

      const contentFragment = this.template.content.cloneNode(true);
      const tempDiv = document.createElement('div');
      tempDiv.appendChild(contentFragment);
      
      const listContainer = tempDiv.querySelector('.shipping-state-list');
      this.buildStateList(listContainer);

      const searchInput = tempDiv.querySelector('.shipping-search-input');
      if (searchInput) {
        searchInput.addEventListener('input', (e) => this.handleSearch(e.target.value, listContainer));
      }

      const popupHeader = `<h3 class="title-popup h5 my-0 px-20 px-md-30 py-20 border-bottom">${this.popupTitleText}</h3>`;
      
      this.initPopup(tempDiv.innerHTML, popupHeader);

      setTimeout(() => {
        this.bindPopupEvents();
      }, 50);
    }

    buildStateList(container) {
      if (!container) return;
      const data = this.getShippingData();
      const regions = data.regions || {};
      const states = Object.keys(regions).sort((a, b) =>
        this.getRegionDisplayName(a).localeCompare(this.getRegionDisplayName(b), this.locale)
      );

      const html = states.map(state => {
        const displayName = this.getRegionDisplayName(state);
        return `
          <li class="shipping-state-item p-15 border-bottom cursor-pointer hover-grey-bg transition"
              role="option"
              tabindex="0"
              data-state="${this.escapeHtml(state)}"
              aria-selected="${state === this.currentState}">
            <span class="fs-14 heading-color">${this.escapeHtml(displayName)}</span>
          </li>
        `;
      }).join('');

      container.innerHTML = html;
    }

    bindPopupEvents() {
      const modalContent = document.querySelector('.tingle-modal--visible .tingle-modal-box__content') || document.body;

      const searchInput = modalContent.querySelector('.shipping-search-input');
      const listContainer = modalContent.querySelector('.shipping-state-list');
      
      if (searchInput && listContainer) {
        searchInput.focus();
        searchInput.addEventListener('input', (e) => this.handleSearch(e.target.value, listContainer));
      }

      const items = modalContent.querySelectorAll('.shipping-state-item');
      items.forEach(item => {
        item.addEventListener('click', () => {
          const selectedState = item.dataset.state;
          localStorage.setItem(this.storageKey, selectedState);
          this.updateEstimation(selectedState);
          this.broadcastUpdate(selectedState);
          const closeBtn = document.querySelector('.tingle-modal__close');
          if (closeBtn) closeBtn.click();
        });

        item.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          item.click();
        });
      });
    }

    handleSearch(query, listContainer) {
      const lowerQuery = query.toLowerCase();
      const items = listContainer.querySelectorAll('.shipping-state-item');
      items.forEach(item => {
        const stateName = item.dataset.state.toLowerCase();
        const displayName = item.textContent.toLowerCase();
        item.style.display = stateName.includes(lowerQuery) || displayName.includes(lowerQuery) ? 'block' : 'none';
      });
    }
  }

  customElements.define('shipping-estimator', ShippingEstimator);
}
